import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Grid, Container, Box } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import useFormValidation from "../../components/Custom/FormValidation";
import { createSoilTestValidationSchema } from "../../components/Custom/ValidationSchema";
import { useAlert } from "../../components/Alert";
import { getSoilTestFields } from "../../Data/fieldData";
import { useNavigate } from "react-router-dom";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import LoaderContainer from "../../components/LoaderContainer";
import { logEvent } from "../../utils/analytics";
import TestModeSelector from "../../components/SoilTest/TestModeSelector";
import SoilTestForm from "../../components/SoilTest/SoilTestForm";
import TestGuidelines from "../../components/SoilTest/TestGuidelines";
import PageHeader from "../../components/SoilTest/PageHeader";

const CreateSoilTest = () => {
  const [farms, setFarms] = useState([]);
  const [crops, setCrops] = useState([]);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [testMode, setTestMode] = useState("device");
  const { t } = useTranslation();

  const validationSchema = createSoilTestValidationSchema(testMode);
  const { errors, validate } = useFormValidation(formData, validationSchema);
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/farmer/farms/")
      .then((res) => {
        const _farms = res.data.map((f) => {
          if (f.farmland) {
            return {
              id: f.farmland.id,
              farm_name: f.farmland.farm_name,
            };
          } else {
            return {
              id: f.id,
              farm_name: f.farm_name,
            };
          }
        });
        setFarms(_farms);
      })
      .catch((error) => {
        console.log("Error fetching farms:", error);
        showAlert("error", t("Failed to load farms"));
      });
  }, []);

  useEffect(() => {
    if (testMode === "manual") {
      if (formData.farm_land) {
        fetchCropsForFarm(formData.farm_land);
      } else {
        setCrops([]);
      }
    } else if (testMode === "device") {
      setCrops([]);
    }
  }, [testMode]);

  const handleChange = (name, value) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    validate({ ...formData, [name]: value });

    if (name === "farm_land" && testMode === "manual" && value) {
      fetchCropsForFarm(value);
    }
  };

  const fetchCropsForFarm = (farmId) => {
    api
      .get("/farmer/crops/")
      .then((res) => {
        const _crops = [];
        const seenCommodities = new Set();

        res.data.forEach((crop) => {
          if (
            crop.farmland &&
            crop.farmland.id == farmId &&
            crop.commodity &&
            !seenCommodities.has(crop.commodity.id)
          ) {
            _crops.push({
              id: crop.id,
              name: crop.commodity.name,
              commodity_id: crop.commodity.id,
            });
            seenCommodities.add(crop.commodity.id);
          }
        });

        setCrops(_crops);

        if (formData.crop && !_crops.find((crop) => crop.id == formData.crop)) {
          setFormData((prev) => ({ ...prev, crop: "" }));
        }
      })
      .catch((error) => {
        console.log("Error fetching crops for farm:", error);
        setCrops([]);
      });
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setTestMode(newMode);
      setFormData({});
    }
  };

  const handleCreation = () => {
    validate(formData);
    if (Object.keys(errors).length === 0) {
      let endpoint;
      let _formData;

      if (testMode === "manual") {
        endpoint = "/soil-testing/create-manual-soil-test-report/";
        _formData = {
          ...formData,
          report_on: new Date().toISOString(),
        };
      } else {
        endpoint = "/soil-testing/test-report/";
        _formData = new FormData();
        for (const key in formData) {
          _formData.append(key, formData[key]);
        }
      }

      const apiRequest = api.post(endpoint, _formData);

      setIsLoading(true);
      apiRequest
        .then((response) => {
          showAlert(
            "success",
            `${testMode === "manual" ? "Manual" : "Device"} Soil Test Created Successfully`
          );
          logEvent("soil_test_created", {
            farm_id: formData.farm_land || formData.farm_id,
            test_report_id: response.data?.id || "unknown",
            test_mode: testMode,
          });
          navigate("/soil-testing");
          setIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          const { response } = error || {};
          const { data } = response || {};
          logEvent("soil_test_creation_failed", {
            farm_id: formData.farm_land || formData.farm_id,
            error_message:
              data?.message ||
              (isNotNullOrEmptyArray(data) ? data[0] : "Unknown error"),
            test_mode: testMode,
          });

          let errorMessage = `Error creating ${testMode} soil test`;
          if (testMode === "manual" && data?.errors) {
            const firstError = Object.values(data.errors)[0];
            if (isNotNullOrEmptyArray(firstError)) {
              errorMessage = firstError[0];
            } else {
              errorMessage = firstError;
            }
          } else if (data?.message) {
            errorMessage = data.message;
          } else if (isNotNullOrEmptyArray(data)) {
            errorMessage = data[0];
          }

          showAlert("error", errorMessage);
          if (testMode === "device" && isNotNullOrEmptyArray(data)) {
            navigate("/soil-testing");
            return;
          }
          setIsLoading(false);
        });
    } else {
      alert("Please fix the validation errors before submitting.");
    }
  };

  if (isLoading) {
    return <LoaderContainer loading={isLoading} />;
  }

  const fields = getSoilTestFields(t, farms, testMode);

  // Update crop options for manual mode
  if (testMode === "manual") {
    const cropField = fields.find((f) => f.name === "crop");
    if (cropField) {
      cropField.options = isNotNullOrEmptyArray(crops)
        ? crops.map((crop) => ({
            value: crop.id,
            label: crop.name,
          }))
        : [
            {
              value: "",
              label: t("Please select a farm first"),
              disabled: true,
            },
          ];
    }
  }

  return (
    <>
      <Helmet>
        <title>Add Soil Test - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <Container maxWidth="md">
        <Box sx={{ py: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <PageHeader />

              <TestModeSelector
                testMode={testMode}
                onModeChange={handleModeChange}
              />

              <SoilTestForm
                testMode={testMode}
                fields={fields}
                formData={formData}
                errors={errors}
                onChange={handleChange}
                onSubmit={handleCreation}
              />

              {testMode === "manual" && <TestGuidelines />}
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default CreateSoilTest;
